import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
const questions = [
// SECTION 1: SYSTEM CALIBRATION (Q1-Q6)
{
  id: 1,
  question: 'When things are unclear, you usually:',
  options: ['Narrow things down and impose structure', 'Keep options open and explore longer'],
  helper: 'Choose what usually happens.'
}, {
  id: 2,
  question: "When something isn't working, you're more likely to:",
  options: ['Push harder and try to make it work', 'Step back and reconsider the direction']
}, {
  id: 3,
  question: 'You feel more stressed by:',
  options: ['Things being undefined or ambiguous', 'Things being locked in or constrained']
}, {
  id: 4,
  question: 'Your bigger risk in work is usually:',
  options: ['Committing too early and feeling stuck', 'Not committing and staying in motion too long']
}, {
  id: 5,
  question: 'When starting something new, you tend to:',
  options: ['Go deep into one direction', 'Try multiple directions in parallel']
}, {
  id: 6,
  question: 'When you hit resistance, your default move is:',
  options: ['Increase control and tighten the system', 'Loosen the system and create space']
},
// SECTION 2: ROLE & POWER CONTEXT (Q7-Q9)
{
  id: 7,
  question: 'Which best describes your current role?',
  options: ['Founder / Owner', 'Manager / Team Lead', 'Individual Contributor', 'Hybrid', 'Between roles']
}, {
  id: 8,
  question: 'How much control do you have over work direction?',
  options: ['I decide most things', 'I influence direction', 'I mostly execute', 'It varies a lot']
}, {
  id: 9,
  question: 'What best describes your current time horizon?',
  options: ['Days to weeks', 'A few months', 'A year or more', 'Unclear / shifting']
},
// SECTION 3: WORK ENVIRONMENT TEXTURE (Q10-Q11)
{
  id: 10,
  question: 'Most of your work happens:',
  options: ['Alone', 'With a small group', 'With many stakeholders', 'Constantly switching']
}, {
  id: 11,
  question: 'Success in your work is mostly measured by:',
  options: ['Output / delivery', 'Decisions made', 'Visibility / perception', 'Stability / continuity']
},
// SECTION 4: NARRATIVE ANCHOR (Q12)
{
  id: 12,
  question: 'Right now, your work life feels closest to:',
  options: ['Building something new', 'Maintaining what exists', 'Resetting after a phase', 'Searching for direction', "Protecting what I've built"]
}];
// Structural convergence animation
const StructuralConvergence = () => {
  return <div className="relative w-full max-w-md mx-auto h-24 sm:h-32" aria-hidden="true" data-id="element-4912">
      <svg viewBox="0 0 400 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" data-id="element-4913">
        <line x1="0" y1="40" x2="400" y2="40" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '0ms'
      }} data-id="element-4914" />
        <line x1="0" y1="60" x2="400" y2="60" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '300ms'
      }} data-id="element-4915" />
        <line x1="0" y1="80" x2="400" y2="80" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '600ms'
      }} data-id="element-4916" />
        <line x1="100" y1="40" x2="100" y2="80" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '900ms'
      }} data-id="element-4917" />
        <line x1="200" y1="40" x2="200" y2="80" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '1200ms'
      }} data-id="element-4918" />
        <line x1="300" y1="40" x2="300" y2="80" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '1500ms'
      }} data-id="element-4919" />
        <circle cx="100" cy="40" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '900ms'
      }} data-id="element-4920" />
        <circle cx="200" cy="40" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1200ms'
      }} data-id="element-4921" />
        <circle cx="300" cy="40" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1500ms'
      }} data-id="element-4922" />
        <circle cx="100" cy="60" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1200ms'
      }} data-id="element-4923" />
        <circle cx="200" cy="60" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1500ms'
      }} data-id="element-4924" />
        <circle cx="300" cy="60" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1800ms'
      }} data-id="element-4925" />
        <circle cx="100" cy="80" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1500ms'
      }} data-id="element-4926" />
        <circle cx="200" cy="80" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1800ms'
      }} data-id="element-4927" />
        <circle cx="300" cy="80" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '2100ms'
      }} data-id="element-4928" />
      </svg>
    </div>;
};
export function WorkMirrorCalibration() {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [isCompiling, setIsCompiling] = useState(false);
  const handleAnswer = (answer: string) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
    if (currentQuestion < questions.length - 1) {
      // Move to next question
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 150);
    } else {
      // All questions answered, start compiling
      setTimeout(() => {
        setIsCompiling(true);
      }, 150);
    }
  };
  useEffect(() => {
    if (isCompiling) {
      // Show compiling screen for 3 seconds minimum, then navigate
      const timer = setTimeout(() => {
        // Store answers in sessionStorage for the result page
        sessionStorage.setItem('workMirrorAnswers', JSON.stringify(answers));
        navigate('/mirrors/work/result');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isCompiling, answers, navigate]);
  if (isCompiling) {
    return <>
        <Helmet data-id="element-4929">
          <title data-id="element-4930">Putting this together... | Lazy Monk Labs</title>
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-4 sm:px-6" data-id="element-4931">
          <div className="max-w-2xl w-full text-center space-y-12 sm:space-y-16" data-id="element-4932">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#F5F5F5]" style={{
            fontFamily: 'Georgia, serif'
          }} data-id="element-4933">
              Putting this together…
            </h1>
            <div className="py-8 sm:py-12" data-id="element-4934">
              <StructuralConvergence data-id="element-4935" />
            </div>
          </div>
        </div>

        <style data-id="element-4936">{`
          @keyframes fade-in {
            from { opacity: 0; stroke-dasharray: 1000; stroke-dashoffset: 1000; }
            to { opacity: 1; stroke-dasharray: 1000; stroke-dashoffset: 0; }
          }
          @keyframes pulse-slow {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 1; }
          }
          .animate-fade-in { animation: fade-in 0.8s ease-out forwards; }
          .animate-pulse-slow { animation: pulse-slow 2s ease-in-out infinite; }
        `}</style>
      </>;
  }
  const question = questions[currentQuestion];
  return <>
      <Helmet data-id="element-4937">
        <title data-id="element-4938">Calibration | Lazy Monk Labs</title>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-4939">
        {/* Question content - centered */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-20 sm:py-24" data-id="element-4940">
          <div className="max-w-2xl w-full space-y-10 sm:space-y-12" data-id="element-4941">
            {/* Question */}
            <div className="space-y-4 sm:space-y-6" data-id="element-4942">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif text-[#F5F5F5] text-center leading-tight" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-4943">
                {question.question}
              </h1>

              {/* Helper text */}
              {question.helper && <p className="text-xs sm:text-sm text-gray-600 text-center" data-id="element-4944">
                  {question.helper}
                </p>}
            </div>

            {/* Options */}
            <div className="space-y-3 sm:space-y-4" data-id="element-4945">
              {question.options.map((option, index) => <button key={index} onClick={() => handleAnswer(option)} className="w-full p-5 sm:p-6 border border-[#2B2B2B] text-left text-sm sm:text-base text-[#F5F5F5] hover:border-[#F5F5F5] hover:bg-[#F5F5F5]/5 transition-all active:scale-[0.99]" data-id="element-4946">
                  {option}
                </button>)}
            </div>
          </div>
        </div>

        {/* Subtle back button */}
        {currentQuestion > 0 && <div className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6" data-id="element-4947">
            <button onClick={() => {
          setCurrentQuestion(currentQuestion - 1);
          setAnswers(answers.slice(0, -1));
        }} className="text-xs sm:text-sm text-gray-600 hover:text-gray-400 transition-colors p-2" data-id="element-4948">
              ← Back
            </button>
          </div>}
      </div>
    </>;
}